import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './CarouselImageCropModal.css';

// Proporção do carrossel (largura/altura) — ajusta ao container que usa object-fit: cover
const CAROUSEL_ASPECT = 1200 / 500;

const ASPECT_OPTIONS = [
  { label: 'Carrossel (recomendado)', value: CAROUSEL_ASPECT },
  { label: 'Livre', value: null },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
  { label: '1:1 (quadrado)', value: 1 }
];

function getCroppedBlob(image, pixelCropNatural, done) {
  if (!pixelCropNatural?.width || !pixelCropNatural?.height || !image) {
    done(null);
    return;
  }
  const { x, y, width, height } = pixelCropNatural;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = Math.round(width);
  canvas.height = Math.round(height);
  ctx.drawImage(
    image,
    Math.round(x), Math.round(y), Math.round(width), Math.round(height),
    0, 0, canvas.width, canvas.height
  );
  canvas.toBlob(blob => done(blob), 'image/jpeg', 0.9);
}

export default function CarouselImageCropModal({ src, onConfirm, onCancel }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [aspectOption, setAspectOption] = useState(CAROUSEL_ASPECT);

  const currentAspect = aspectOption === null ? undefined : aspectOption;

  const buildInitialCrop = useCallback((naturalWidth, naturalHeight, aspect) => {
    if (aspect == null) {
      const w = 80;
      const h = 60;
      return { unit: '%', width: w, height: h, x: (100 - w) / 2, y: (100 - h) / 2 };
    }
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, naturalWidth, naturalHeight),
      naturalWidth,
      naturalHeight
    );
  }, []);

  const onImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    const aspectForCrop = aspectOption === null ? null : (currentAspect ?? CAROUSEL_ASPECT);
    const cropInitial = buildInitialCrop(naturalWidth, naturalHeight, aspectForCrop);
    setCrop(cropInitial);
    setCompletedCrop(cropInitial);
  }, [aspectOption, currentAspect, buildInitialCrop]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !img.complete || !img.naturalWidth) return;
    const aspect = aspectOption === null ? undefined : aspectOption;
    const cropInitial = buildInitialCrop(img.naturalWidth, img.naturalHeight, aspect ?? CAROUSEL_ASPECT);
    setCrop(cropInitial);
    setCompletedCrop(cropInitial);
  }, [aspectOption, buildInitialCrop]);

  const handleAplicar = useCallback(() => {
    const img = imgRef.current;
    if (!img || !completedCrop?.width || !completedCrop?.height) {
      onConfirm(null);
      return;
    }
    const { naturalWidth, naturalHeight, clientWidth, clientHeight } = img;
    const scaleX = naturalWidth / clientWidth;
    const scaleY = naturalHeight / clientHeight;
    const pixelCropDisplay = completedCrop.unit === 'px'
      ? completedCrop
      : convertToPixelCrop(completedCrop, clientWidth, clientHeight);
    const pixelCropNatural = {
      x: pixelCropDisplay.x * scaleX,
      y: pixelCropDisplay.y * scaleY,
      width: pixelCropDisplay.width * scaleX,
      height: pixelCropDisplay.height * scaleY
    };
    getCroppedBlob(img, pixelCropNatural, (blob) => {
      onConfirm(blob);
    });
  }, [completedCrop, onConfirm]);

  return (
    <div className="carousel-crop-modal-overlay" onClick={onCancel}>
      <div className="carousel-crop-modal" onClick={e => e.stopPropagation()}>
        <h3>Recortar imagem para o carrossel</h3>
        <p className="carousel-crop-hint">
          Use o zoom para aproximar a imagem e a proporção para definir o formato da área de recorte.
        </p>

        <div className="carousel-crop-controls">
          <div className="carousel-crop-control-group">
            <label>Zoom</label>
            <div className="carousel-crop-zoom-row">
              <input
                type="range"
                min="0.5"
                max="2.5"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="carousel-crop-zoom-slider"
              />
              <span className="carousel-crop-zoom-value">{Math.round(zoom * 100)}%</span>
            </div>
          </div>
          <div className="carousel-crop-control-group">
            <label>Proporção do recorte</label>
            <select
              value={aspectOption === null ? 'free' : String(aspectOption)}
              onChange={(e) => {
                const v = e.target.value;
                setAspectOption(v === 'free' ? null : Number(v));
              }}
              className="carousel-crop-aspect-select"
            >
              {ASPECT_OPTIONS.map((opt) => (
                <option key={opt.label} value={opt.value === null ? 'free' : String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="carousel-crop-area">
          <div
            className="carousel-crop-zoom-wrap"
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'center center'
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
              aspect={currentAspect}
              keepSelection
            >
              <img
                ref={imgRef}
                src={src}
                alt="Recorte"
                onLoad={onImageLoad}
                style={{ maxHeight: '50vh', width: 'auto' }}
              />
            </ReactCrop>
          </div>
        </div>
        <div className="carousel-crop-actions">
          <button type="button" onClick={onCancel} className="btn-cancel">Cancelar</button>
          <button type="button" onClick={handleAplicar} className="btn-confirm">Aplicar recorte</button>
          <button type="button" onClick={handleAplicar} className="btn-salvar">Salvar</button>
        </div>
      </div>
    </div>
  );
}
